import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Paper,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { supabase } from '../../config/supabase'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import { Location } from '../../types'

interface LocationFormData {
  id?: string
  name: string
  type: 'region' | 'kommune' | 'city'
  parent_id?: string | null
  postal_codes?: string[]
  latitude?: number | null
  longitude?: number | null
  headline: string
  top_text: string
  bottom_text: string
  slug: string
  dawa_region_kode?: string | null
  dawa_kommune_kode?: string | null
  seo_title?: string | null
  seo_metadesc?: string | null
  canonical_url?: string | null
}

export default function LocationForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    type: 'city',
    parent_id: null,
    postal_codes: [],
    latitude: null,
    longitude: null,
    headline: '',
    top_text: '',
    bottom_text: '',
    slug: '',
    seo_title: '',
    seo_metadesc: '',
    canonical_url: '',
  })

  const [parentLocations, setParentLocations] = useState<Location[]>([])
  const [hierarchyPath, setHierarchyPath] = useState<string[]>([])
  const [fetchingData, setFetchingData] = useState(false)

  useEffect(() => {
    if (isEdit && id) {
      fetchLocation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit])
  
  useEffect(() => {
    fetchParentLocations(formData.type)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type])

  useEffect(() => {
    // Auto-generate slug from name
    if (formData.name && !isEdit) {
      const slug = generateSlug(formData.name, formData.type)
      setFormData(prev => ({ ...prev, slug }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name, formData.type])

  useEffect(() => {
    // Fetch hierarchy path when parent changes
    if (formData.parent_id) {
      fetchHierarchyPath(formData.parent_id)
    } else {
      setHierarchyPath([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.parent_id])

  const generateSlug = (name: string, type: string): string => {
    const base = name
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'oe')
      .replace(/å/g, 'aa')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

    if (type === 'city') return `${base}-by`
    if (type === 'kommune') return `${base}-kommune`
    return base
  }

  const fetchLocation = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      if (data) setFormData(data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchParentLocations = async (type?: string) => {
    try {
      // Fetch potential parents based on type
      const currentType = type || formData.type
      let parentType: 'region' | 'kommune' | null = null

      if (currentType === 'kommune') parentType = 'region'
      if (currentType === 'city') parentType = 'kommune'

      if (!parentType) {
        setParentLocations([])
        return
      }

      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('type', parentType)
        .order('name')

      if (error) throw error
      setParentLocations(data || [])
    } catch (err: any) {
      console.error('Error fetching parents:', err)
    }
  }

  const fetchHierarchyPath = async (parentId: string) => {
    try {
      // Build hierarchy path by walking up parents
      const path: string[] = []
      let currentId: string | null = parentId

      while (currentId) {
        type LocationData = { name: string; parent_id: string | null }
        const { data, error }: { data: LocationData | null; error: any } = await supabase
          .from('locations')
          .select('name, parent_id')
          .eq('id', currentId)
          .single()

        if (error || !data) break

        path.unshift(data.name)
        currentId = data.parent_id
      }

      setHierarchyPath(path)
    } catch (err) {
      console.error('Error building hierarchy:', err)
    }
  }

  const handleChange = (field: keyof LocationFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value })
  }

  const handleTypeChange = (type: 'region' | 'kommune' | 'city') => {
    setFormData({
      ...formData,
      type,
      parent_id: type === 'region' ? null : formData.parent_id,
    })
    // Refresh parent options
    fetchParentLocations(type)
  }

  // Auto-fetch coordinates and parent from DAWA when postal code is entered
  const handlePostalCodeChange = async (postalCode: string) => {
    if (!postalCode || postalCode.length !== 4) return

    setFetchingData(true)
    try {
      const response = await fetch(`https://api.dataforsyningen.dk/postnumre/${postalCode}?struktur=nestet`)
      
      if (response.ok) {
        const data = await response.json()
        
        // Set coordinates
        if (data.visueltcenter && data.visueltcenter.length === 2) {
          setFormData(prev => ({
            ...prev,
            longitude: data.visueltcenter[0],
            latitude: data.visueltcenter[1],
          }))
        }
        
        // Auto-detect parent kommune if type is 'city'
        if (formData.type === 'city' && data.kommune?.kode) {
          const { data: kommune } = await supabase
            .from('locations')
            .select('id, name')
            .eq('dawa_kommune_kode', data.kommune.kode)
            .eq('type', 'kommune')
            .single()
          
          if (kommune) {
            setFormData(prev => ({
              ...prev,
              parent_id: kommune.id,
            }))
          }
        }
        
        // Auto-detect parent region if type is 'kommune'
        if (formData.type === 'kommune' && data.region?.kode) {
          const { data: region } = await supabase
            .from('locations')
            .select('id, name')
            .eq('dawa_region_kode', data.region.kode)
            .eq('type', 'region')
            .single()
          
          if (region) {
            setFormData(prev => ({
              ...prev,
              parent_id: region.id,
            }))
          }
        }
      }
    } catch (err) {
      console.error('Error fetching DAWA data:', err)
    } finally {
      setFetchingData(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(false)

    const canonical = formData.canonical_url?.trim()
    if (canonical && !/^https?:\/\/.+/.test(canonical)) {
      setError('Canonical URL skal være en absolut URL (starte med https://)')
      setSaving(false)
      return
    }

    try {
      const submitData = {
        ...formData,
        parent_id: formData.type === 'region' ? null : formData.parent_id,
        // Empty = revert to defaults on WP (Yoast template / self-canonical)
        seo_title: formData.seo_title?.trim() || null,
        seo_metadesc: formData.seo_metadesc?.trim() || null,
        canonical_url: canonical || null,
      }

      if (isEdit) {
        const { error } = await supabase
          .from('locations')
          .update(submitData)
          .eq('id', id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('locations')
          .insert([submitData])

        if (error) throw error
      }

      setSuccess(true)
      setTimeout(() => {
        navigate('/locations')
      }, 1500)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/locations')}
          sx={{ minWidth: 'auto' }}
        >
          Tilbage
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? 'Rediger lokation' : 'Opret ny lokation'}
        </Typography>
      </Box>

      {/* Hierarchy Breadcrumb */}
      {hierarchyPath.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Hierarki
          </Typography>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            {hierarchyPath.map((name, index) => (
              <Typography key={index} variant="body2" sx={{ fontWeight: 600 }}>
                {name}
              </Typography>
            ))}
            <Typography variant="body2" color="text.secondary">
              {formData.name || 'Ny lokation'}
            </Typography>
          </Breadcrumbs>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Lokation gemt! Omdirigerer...
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            {/* Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => handleTypeChange(e.target.value as any)}
                  disabled={isEdit}
                >
                  <MenuItem value="region">Region</MenuItem>
                  <MenuItem value="kommune">Kommune</MenuItem>
                  <MenuItem value="city">By</MenuItem>
                </Select>
              </FormControl>
              {isEdit && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Type kan ikke ændres efter oprettelse
                </Typography>
              )}
            </Box>

            {/* Parent Selection */}
            {formData.type !== 'region' && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>
                    {formData.type === 'kommune' ? 'Region' : 'Kommune'}
                  </InputLabel>
                  <Select
                    value={formData.parent_id || ''}
                    label={formData.type === 'kommune' ? 'Region' : 'Kommune'}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  >
                    {parentLocations.map((parent) => (
                      <MenuItem key={parent.id} value={parent.id}>
                        {parent.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Name */}
            <Box sx={{ mb: 3 }}>
              <TextField
                required
                fullWidth
                label="Navn"
                value={formData.name}
                onChange={handleChange('name')}
                placeholder={
                  formData.type === 'region'
                    ? 'F.eks. Region Hovedstaden'
                    : formData.type === 'kommune'
                    ? 'F.eks. København'
                    : 'F.eks. København K'
                }
              />
            </Box>

            {/* Slug */}
            <Box sx={{ mb: 3 }}>
              <TextField
                required
                fullWidth
                label="Slug (URL-venlig)"
                value={formData.slug}
                onChange={handleChange('slug')}
                helperText="Autogenereret fra navn, kan tilpasses"
              />
            </Box>

            {/* Postal Codes (Cities only) */}
            {formData.type === 'city' && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Postnummer (til auto-hentning)"
                  placeholder="8000"
                  helperText={fetchingData ? 'Henter data fra DAWA...' : 'Indtast postnummer for at auto-hente koordinater og kommune'}
                  onChange={(e) => {
                    const code = e.target.value.trim()
                    if (code.length === 4) {
                      handlePostalCodeChange(code)
                    }
                  }}
                  inputProps={{ maxLength: 4 }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Postnumre (kommasepareret)"
                  value={formData.postal_codes?.join(', ') || ''}
                  onChange={(e) => {
                    const codes = e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    setFormData({ ...formData, postal_codes: codes })
                  }}
                  placeholder="8000, 8200, 8210"
                  helperText="For byer der dækker flere postnumre (kan også bare være ét)"
                />
              </Box>
            )}

            {/* Coordinates */}
            <Box sx={{ mb: 3 }}>
              {formData.latitude && formData.longitude && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  📍 Koordinater hentet: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  type="number"
                  label="Latitude"
                  value={formData.latitude || ''}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || null })}
                  inputProps={{ step: 0.000001 }}
                />
                <TextField
                  type="number"
                  label="Longitude"
                  value={formData.longitude || ''}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || null })}
                  inputProps={{ step: 0.000001 }}
                />
              </Box>
            </Box>

            {/* Headline */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Headline"
                value={formData.headline}
                onChange={handleChange('headline')}
                placeholder={`F.eks. Bedemand i ${formData.name || '...'}`}
                helperText="H1 titel på siden"
              />
            </Box>

            {/* SEO */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                SEO
              </Typography>
              <TextField
                fullWidth
                label="SEO titel"
                value={formData.seo_title || ''}
                onChange={handleChange('seo_title')}
                error={(formData.seo_title?.length || 0) > 60}
                helperText={`${formData.seo_title?.length || 0}/60 tegn${(formData.seo_title?.length || 0) > 60 ? ' – for lang, Google afkorter' : ''}. Tom = Yoast-skabelon på WP.`}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Meta-beskrivelse"
                value={formData.seo_metadesc || ''}
                onChange={handleChange('seo_metadesc')}
                error={(formData.seo_metadesc?.length || 0) > 155}
                helperText={`${formData.seo_metadesc?.length || 0}/155 tegn${(formData.seo_metadesc?.length || 0) > 155 ? ' – for lang, Google afkorter' : ''}. Tom = Yoast-skabelon på WP.`}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="url"
                label="Canonical URL"
                value={formData.canonical_url || ''}
                onChange={handleChange('canonical_url')}
                placeholder="https://find-bedemand.dk/lokation/..."
                error={Boolean(formData.canonical_url?.trim() && !/^https?:\/\/.+/.test(formData.canonical_url.trim()))}
                helperText="Absolut URL. Bruges på by-sider der peger på kommune-siden. Tom = self-canonical."
              />
            </Box>

            {/* Top Text */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Top Text
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                Tekst der vises øverst på siden
              </Typography>
              <RichTextEditor
                value={formData.top_text}
                onChange={(html) => setFormData({ ...formData, top_text: html })}
                placeholder="Indtast tekst..."
              />
            </Box>

            {/* Bottom Text */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Bottom Text
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                Tekst der vises nederst på siden
              </Typography>
              <RichTextEditor
                value={formData.bottom_text}
                onChange={(html) => setFormData({ ...formData, bottom_text: html })}
                placeholder="Indtast tekst..."
              />
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/locations')}
            disabled={saving}
          >
            Annuller
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Gemmer...' : 'Gem lokation'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

